"use client";

import type { ReactNode } from "react";
import {
	CONTENT_TOOLTIP_SHORT,
	ContentTooltip,
} from "./design-system/tooltip";
import { useId } from "react";
import { cn } from "../lib";
import { ToggleSwitch } from "./custom/toggle-switch";
import { LIVE_SAVING_SUCCESS_TICK_DURATION_MS } from "./custom/use-live-saving-phase";
import { Label } from "./label";
const LIVE_SAVING_BOOLEAN_OPTIONS = [
	{ id: "off" as const, ariaLabel: "Off", label: "Off" },
	{ id: "on" as const, ariaLabel: "On", label: "On" },
];

export interface LiveSavingSwitchProps {
	id?: string;
	label: string;
	/** @deprecated Prefer `infoNote` for toggle helper copy. */
	description?: string;
	infoNote?: ReactNode;
	checked: boolean;
	mixed?: boolean;
	mixedTooltip?: ReactNode;
	disabled?: boolean;
	successTickDurationMs?: number;
	className?: string;
	descriptionClassName?: string;
	layoutId?: string;
	onCheckedChange: (checked: boolean) => Promise<void>;
}

export function LiveSavingSwitch({
	id,
	label,
	description,
	infoNote,
	checked,
	mixed = false,
	mixedTooltip,
	disabled = false,
	successTickDurationMs = LIVE_SAVING_SUCCESS_TICK_DURATION_MS,
	className,
	descriptionClassName = "text-sm text-muted-foreground",
	layoutId,
	onCheckedChange,
}: LiveSavingSwitchProps) {
	const generatedId = useId();
	const resolvedId = id ?? generatedId;
	const resolvedLayoutId = layoutId ?? `live-saving-${resolvedId}`;

	const toggle = (
		<ToggleSwitch
			className="shrink-0"
			layoutId={resolvedLayoutId}
			mixed={mixed}
			optionDisplay="text"
			highlightWhenRightActive
			value={checked ? "on" : "off"}
			disabled={disabled}
			successTickDurationMs={successTickDurationMs}
			options={LIVE_SAVING_BOOLEAN_OPTIONS}
			onValueChange={async (next) => {
				await onCheckedChange(next === "on");
			}}
		/>
	);

	return (
		<div
			className={cn(
				"flex w-full items-center justify-between gap-4",
				className,
			)}
		>
			<div className="min-w-0 flex-1 space-y-1">
				<Label htmlFor={resolvedId}>{label}</Label>
				{infoNote}
				{description ? (
					<p className={descriptionClassName}>{description}</p>
				) : null}
			</div>
			{mixed && mixedTooltip ? (
								<ContentTooltip
					{...CONTENT_TOOLTIP_SHORT}
					side="left"
					contentClassName="max-w-xs whitespace-pre-line"
					content={mixedTooltip}
				>
					<span className="inline-flex shrink-0">
													{toggle}
												</span>
				</ContentTooltip>
			) : (
				toggle
			)}
		</div>
	);
}
